import type { AxiosRequestConfig } from 'axios';
import { api, axiosInstance } from '@/lib/api';

/** Jeton `?accessToken=` du lien apprenant : le backend l’accepte en query (sans session Lyrra). */
function learnerAccessAxiosConfig(accessToken?: string | null): AxiosRequestConfig | undefined {
  const token = accessToken?.trim();
  if (!token) return undefined;
  return {
    skipDefaultAuth: true,
    params: { accessToken: token },
  };
}

export const eduflowApi = {
  // Flows
  listFlows: (params?: { page?: number; limit?: number; search?: string; status?: string }) =>
    api.get('/eduflows', params),
  getFlow: (id: string) =>
    api.get(`/eduflows/${id}`),
  createFlow: (data: { title: string; description?: string }) =>
    api.post('/eduflows', data),
  updateFlow: (id: string, data: unknown) =>
    api.put(`/eduflows/${id}`, data),
  deleteFlow: (id: string) =>
    api.delete(`/eduflows/${id}`),

  // Publishing
  publishFlow: (id: string) =>
    api.post(`/eduflows/${id}/publish`),
  unpublishFlow: (id: string) =>
    api.post(`/eduflows/${id}/unpublish`),

  // Blocks
  getBlocks: (flowId: string) =>
    api.get(`/eduflows/${flowId}/blocks`),
  addBlock: (flowId: string, data: unknown) =>
    api.post(`/eduflows/${flowId}/blocks`, data),
  updateBlock: (flowId: string, blockId: string, data: unknown) =>
    api.put(`/eduflows/${flowId}/blocks/${blockId}`, data),
  deleteBlock: (flowId: string, blockId: string) =>
    api.delete(`/eduflows/${flowId}/blocks/${blockId}`),

  // Versions
  listVersions: (flowId: string) =>
    api.get(`/eduflows/${flowId}/versions`),
  createVersion: (flowId: string, type: 'major' | 'minor') =>
    api.post(`/eduflows/${flowId}/versions`, { type }),
  activateVersion: (flowId: string, versionId: string) =>
    api.post(`/eduflows/${flowId}/versions/${versionId}/activate`, {}),
  restoreVersion: (flowId: string, versionId: string) =>
    api.post(`/eduflows/${flowId}/versions/${versionId}/restore`, {}),
  duplicateVersion: (flowId: string, versionId: string, type: 'major' | 'minor') =>
    api.post(`/eduflows/${flowId}/versions/${versionId}/duplicate`, { type }),
  deleteVersion: (flowId: string, versionId: string) =>
    api.delete(`/eduflows/${flowId}/versions/${versionId}`),

  // Share
  getShareSettings: (flowId: string) =>
    api.get(`/eduflows/${flowId}/share`),
  updateShareSettings: (flowId: string, settings: unknown) =>
    api.put(`/eduflows/${flowId}/share`, settings),
  listDemoConsultations: (flowId: string) =>
    api.get<{
      success?: boolean;
      data?: {
        flowId: string;
        isDemo: boolean;
        title: string | null;
        total: number;
        items: Array<{
          id: string;
          firstName: string;
          lastName: string;
          email: string;
          phone: string | null;
          acceptedTerms: boolean;
          createdAt: string;
        }>;
      };
    }>(`/eduflows/${flowId}/demo/consultations`),
  getAvailableLearners: (flowId: string, search?: string) =>
    api.get(`/eduflows/${flowId}/learners/available`, search ? { search } : {}),
  assignLearner: (flowId: string, payload: { learnerId?: string; firstName?: string; lastName?: string; email?: string }) =>
    api.post(`/eduflows/${flowId}/learners`, payload),
  getMyAssignedFlows: () =>
    api.get('/eduflows/my'),
  getLearnerAccessLink: (flowId: string) =>
    api.post(`/eduflows/${flowId}/learner-access-link`),

  // Designer
  getDesignerData: (flowId: string) =>
    api.get(`/eduflows/${flowId}/designer`),
  saveDesignerLayout: (flowId: string, layout: unknown) =>
    api.put(`/eduflows/${flowId}/designer/layout`, layout),
  autoLayout: (flowId: string) =>
    api.post(`/eduflows/${flowId}/designer/auto-layout`),

  designerAiChat: (
    flowId: string,
    body: {
      messages: Array<{ role: 'user' | 'assistant'; content: string }>;
      flowSnapshot: { blocks: unknown[]; edges: unknown[]; objectives?: unknown[] };
      sourceDocument?: {
        name: string;
        mimeType: string;
        text: string;
        isStructuredTraining?: boolean;
      };
      autoApply?: boolean;
      /** Sync avec le designer : focus Propriétés + multi-sélection canevas. */
      designerContext?: {
        primarySelectedBlockId?: string | null;
        selectedBlockIds?: string[];
      };
      courseContext?: {
        title?: string;
        description?: string | null;
        language?: string;
      };
      designerSkills?: Array<'path_validate' | 'quiz_mcq_strict'>;
      webSources?: Array<{ url: string }>;
    },
  ) =>
    api.post<{
      success: boolean;
      data: {
        message: string;
        operations: unknown[] | null;
        needsUserConfirmation: boolean;
        followUpPrompts?: string[];
        parseWarning?: string;
      };
    }>(`/eduflows/${flowId}/designer/ai-chat`, body, { timeout: 120_000 }),

  uploadDesignerAiDocument: (flowId: string, file: File) => {
    const fd = new FormData();
    fd.append('document', file);
    return axiosInstance
      .post<{
        success: boolean;
        data: {
          name: string;
          mimeType: string;
          text: string;
          isStructuredTraining: boolean;
        };
      }>(`/eduflows/${flowId}/designer/ai-document`, fd)
      .then((r) => r.data);
  },

  // Import/Export (.lyrra = ZIP avec médias ; import JSON historique via importFlow)
  exportFlow: (flowId: string) =>
    axiosInstance
      .post(`/eduflows/${flowId}/export`, {}, { responseType: 'arraybuffer' })
      .then((r) => r.data as ArrayBuffer),

  exportFlowJsonLight: (flowId: string) =>
    axiosInstance
      .get(`/eduflows/${flowId}/export/json-light`, { responseType: 'blob' })
      .then((r) => r.data as Blob),

  importPackage: (file: File) => {
    const fd = new FormData();
    fd.append('file', file);
    return axiosInstance.post<unknown>('/eduflows/import/package', fd).then((r) => r.data);
  },

  importFlow: (data: unknown) =>
    api.post('/eduflows/import', data),

  importFlowJsonLight: (data: unknown) =>
    api.post<{ id: string; creationMode: 'simple' | 'advanced' }>('/eduflows/import/json-light', data),

  // Demandes de déblocage (limite de pertes de focus dépassée)
  createUnblockRequest: (
    flowId: string,
    data: { message?: string; focusLossCount?: number },
    accessToken?: string | null,
  ) => api.post(`/eduflows/${flowId}/unblock-requests`, data, learnerAccessAxiosConfig(accessToken)),

  getMyUnblockRequest: (flowId: string, accessToken?: string | null) =>
    api.get(`/eduflows/${flowId}/unblock-requests/my`, undefined, learnerAccessAxiosConfig(accessToken)),

  listPendingUnblockRequests: () =>
    api.get(`/eduflows/unblock-requests/pending`),

  approveUnblockRequest: (requestId: string) =>
    api.post(`/eduflows/unblock-requests/${requestId}/approve`),

  rejectUnblockRequest: (requestId: string) =>
    api.post(`/eduflows/unblock-requests/${requestId}/reject`),

  consumeUnblockRequest: (requestId: string, accessToken?: string | null) =>
    api.post(
      `/eduflows/unblock-requests/${requestId}/consume`,
      undefined,
      learnerAccessAxiosConfig(accessToken),
    ),

  /** Inscription publique : bloc formulaire marqué « formulaire d'inscription ». */
  submitPublicRegistrationForm: (
    flowId: string,
    data: { blockId: string; values: Record<string, unknown> },
  ) => api.post(`/eduflows/${flowId}/public/registration-form`, data),

  // Player session (F2)
  startPlaySession: (flowId: string) =>
    api.post<{ sessionId: string }>(`/eduflows/${flowId}/play/start`),

  getPlayProgress: (flowId: string) =>
    api.get(`/eduflows/${flowId}/play`),

  syncProgress: (flowId: string, data: {
    sessionId: string | null;
    eventType?: string;
    currentBlockId: string | null;
    completedBlockIds: string[];
    completionPercentage?: number;
    navigableBlockCount?: number;
    totalTimeSpent: number;
    score: number;
    maxScore: number;
    blockTimeSpent: Record<string, number>;
    navigationHistory?: string[];
    responses?: Record<string, unknown>;
  }) =>
    api.post(`/eduflows/${flowId}/play/progress`, data),

  trackPlayEvent: (flowId: string, data: {
    sessionId?: string | null;
    eventType:
      | 'training_enter'
      | 'training_exit'
      | 'training_completed'
      | 'block_enter'
      | 'block_exit'
      | 'response_submitted'
      | 'progress_sync';
    blockId?: string;
    currentBlockId?: string | null;
    status?: 'started' | 'completed' | 'skipped' | 'in_progress' | 'exited';
    response?: unknown;
    timeSpent?: number;
    totalTimeSpent?: number;
    score?: number;
    maxScore?: number;
    completedBlockIds?: string[];
    blockTimeSpent?: Record<string, number>;
    navigationHistory?: string[];
    metadata?: Record<string, unknown>;
  }) =>
    api.post(`/eduflows/${flowId}/play/progress`, data),

  completePlaySession: (flowId: string, data: {
    sessionId: string | null;
    totalTimeSpent: number;
    totalScore: number;
    maxScore: number;
    completedBlockIds: string[];
  }) =>
    api.post(`/eduflows/${flowId}/play/complete`, data),

  getOrCreateSimplifiedPlayText: (
    flowId: string,
    data: { mode?: 'falc' | 'dys'; lang?: string; blockIds?: string[] },
    options?: { isPublicMode?: boolean; publicFlowCode?: boolean; axiosConfig?: AxiosRequestConfig },
  ) =>
    api.post<{
      mode: 'falc' | 'dys';
      flowId: string;
      versionId: string | null;
      generatedCount: number;
      reusedCount: number;
      blocks: Array<{ id: string; title: string | null; data: Record<string, unknown> }>;
    }>(
      options?.isPublicMode
        ? options.publicFlowCode
          ? `/eduflows/public/code/${flowId}/simplified-text`
          : `/eduflows/${flowId}/public/simplified-text`
        : `/eduflows/${flowId}/play/simplified-text`,
      data,
      { timeout: 120_000, ...(options?.axiosConfig ?? {}) },
    ),

  /** V2 — reconnaissance faciale (serveur : comparaison robuste, référence résolue côté API) */
  verifyFace: (
    flowId: string,
    blockId: string,
    body: { capturedImageBase64: string; similarityThreshold?: number },
  ) =>
    api.post<{
      success?: boolean;
      data?: {
        verified: boolean;
        similarity: number;
        threshold: number;
        method: 'rekognition' | 'enhanced_server';
        referenceSource: 'block_settings' | 'participant_profile';
      };
    }>(`/eduflows/${flowId}/blocks/${blockId}/face-verify`, body),

  /** Notation d’une réponse texte (référence + IA ou comparaison exacte, lue côté serveur). */
  scoreEvaluationSemanticAnswer: (
    flowId: string,
    blockId: string,
    body: { questionId: string; learnerAnswer: string },
  ) =>
    api.post<{
      success?: boolean;
      data?: {
        score: number;
        passed: boolean;
        rationale: string;
        threshold: number;
        gradingMode: 'semantic' | 'exact' | 'ungraded';
        pointsEarned: number;
        questionPoints: number;
      };
    }>(`/eduflows/${flowId}/blocks/${blockId}/evaluation/semantic-score`, body),

  /** Chatbot RAG (documents indexés, réponses basées sur le corpus) */
  getChatbotRagConfig: (flowId: string) =>
    api.get<{
      success?: boolean;
      data?: {
        enabled: boolean;
        documentCount: number;
        canEnable: boolean;
        vectorConfigured: boolean;
      };
    }>(`/eduflows/${flowId}/chatbot/config`),

  toggleChatbotRag: (flowId: string, enabled: boolean) =>
    api.post<{ success?: boolean; data?: { enabled: boolean } }>(`/eduflows/${flowId}/chatbot/toggle`, {
      enabled,
    }),

  listChatbotRagDocuments: (flowId: string) =>
    api.get<{
      success?: boolean;
      data?: { documents: Array<{ id: string; filename: string; fileSize: number | null; mimeType: string | null; createdAt: string }> };
    }>(`/eduflows/${flowId}/chatbot/documents`),

  uploadChatbotRagDocument: (flowId: string, file: File) => {
    const fd = new FormData();
    fd.append('document', file);
    return axiosInstance
      .post<{
        success?: boolean;
        data?: {
          document: { id: string; filename: string };
          indexed: boolean;
          ragIndexFailed?: boolean;
        };
      }>(`/eduflows/${flowId}/chatbot/documents`, fd)
      .then((r) => r.data);
  },

  reindexChatbotRagDocuments: (flowId: string) =>
    api.post<{
      success?: boolean;
      data?: {
        total: number;
        indexed: number;
        failed: number;
      };
    }>(`/eduflows/${flowId}/chatbot/documents/reindex`, {}),

  deleteChatbotRagDocument: (flowId: string, docId: string) =>
    api.delete(`/eduflows/${flowId}/chatbot/documents/${docId}`),

  // Templates
  listTemplates: (params?: { page?: number; limit?: number }) =>
    api.get('/eduflows/templates', params),
  createFromTemplate: (templateId: string, body: { title: string; description?: string | null }) =>
    api.post(`/eduflows/from-template/${templateId}`, body),
};
